import { IPDefaultButton, IPPrimaryButton } from 'baseComponents';
import { IPPanel, IPPanelType } from 'baseComponents/Panel/IPPanel';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalLayerRightSideBarProps, globalStackStylesPanelFooter, globalStackTokensGapSmall } from 'globalStyles';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ITagFormHandlers {
  // The form calls these to register its save action + disabled state, which drive the panel footer.
  onSaveCallback: (save: () => void | Promise<void>) => void;
  onSaveDisabledCallback: (isDisabled: () => boolean) => void;
}

interface ITagFormPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: (handlers: ITagFormHandlers) => ReactNode;
}

//
// Shared side-panel shell for tag create + edit: same chrome as the KPI/Forms field panel, with a
// pinned Save/Cancel footer. The form (TagCreator / TagEditor) registers its save + disabled state via
// the render-prop handlers, so this panel + footer wiring lives in exactly one place.
//
const TagFormPanel = (props: ITagFormPanelProps) => {
  const { t } = useTranslation(['translation']);
  const [saveCallback, setSaveCallback] = useState<(() => void | Promise<void>) | undefined>(undefined);
  const [saveDisabledCallback, setSaveDisabledCallback] = useState<(() => boolean) | undefined>(undefined);

  const onRenderFooterContent = () => (
    <IPStack horizontal wrap styles={globalStackStylesPanelFooter} tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPPrimaryButton
          disabled={saveDisabledCallback ? saveDisabledCallback() : true}
          onClick={() => saveCallback?.()}
          text={t('translation:General.Button.Save')}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPDefaultButton onClick={props.onClose} text={t('translation:General.Button.Cancel')} />
      </IPStack.Item>
    </IPStack>
  );

  return (
    <IPPanel
      headerText={props.title}
      type={IPPanelType.medium}
      isOpen={props.isOpen}
      isBlocking={true}
      isFooterAtBottom={true}
      onRenderFooterContent={onRenderFooterContent}
      hasCloseButton={false}
      layerProps={globalLayerRightSideBarProps}
      focusTrapZoneProps={{ forceFocusInsideTrap: false }}
    >
      {props.children({
        onSaveCallback: (cb) => setSaveCallback(() => cb),
        onSaveDisabledCallback: (cb) => setSaveDisabledCallback(() => cb),
      })}
    </IPPanel>
  );
};

export default TagFormPanel;
